<template>
  <div data-component-group="filter-forms" data-component="payment">
    <FiltersForm
      :has-filters="hasFilters"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="selectedCountries"
    >
      <v-text-field
        clearable
        v-model="search"
        type="search"
        label="Search"
        prepend-inner-icon="search"
      />

      <v-autocomplete
        v-if="!isCreatorProgramPage"
        clearable
        v-model="contract"
        :items="contracts"
        :custom-filter="customIdFilter"
        :loading="isLoadingContracts"
        prepend-inner-icon="event"
        :return-object="true"
        :item-title="contractName"
        label="Price contract ID"
        v-model:search="contractSearch"
      />

      <v-autocomplete
        v-if="!isCreatorProgramPage"
        clearable
        v-model="advertiser"
        :items="advertisers"
        :loading="isLoadingAdvertisers"
        prepend-inner-icon="add_business"
        :return-object="true"
        item-title="name"
        :label="$t('advertiser')"
        v-model:search="advertiserSearch"
      />

      <base-date-picker
        :date="date"
        placeholder="Created at"
        :config="dateConfig"
        :on-change="onChangeDate"
      />

      <v-autocomplete
        clearable
        v-model="business"
        :items="businesses"
        :loading="isLoadingBusinesses"
        prepend-inner-icon="domain"
        label="Business"
        item-title="name"
        item-value="id"
        v-model:search="businessSearch"
        @click:clear="getBusinesses()"
        @focus="!businesses?.length && getBusinesses()"
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import FiltersForm from './FiltersBase';
  import isDateRange from '../../../validators/dateRange';
  import { isNum, isStr, isNull, debounce, escapeSearch } from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  const businessSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500,
  });

  // TODO: Translate component
  export default {
    components: {
      FiltersForm,
    },
    emits: ['clearFilters', 'filter'],
    data() {
      return {
        search: null,
        contract: null,
        advertiser: null,
        contractSearch: '',
        business: null,
        businessSearch: '',
        advertiserSearch: '',
        date: null,
        selectedCountries: [],
        dateConfig: {
          mode: 'range',
        },
        isLoadingContracts: false,
        isRefreshingContracts: false,
        isLoadingBusinesses: false,
        isRefreshingBusinesses: false,
        isLoadingAdvertisers: false,
        isRefreshingAdvertisers: false,
      };
    },
    computed: {
      contracts() {
        return this.$store.getters['contracts/items'];
      },
      advertisers() {
        return this.$store.getters['promotions/advertisers'];
      },
      hasFilters() {
        const {
          search,
          contract = {},
          advertiser = {},
          selectedCountries = [],
          date,
          business
        } = this;

        return (
          !!search ||
          isNum(contract?.id) ||
          !!date ||
          selectedCountries.length > 0 ||
          isNum(advertiser?.id) ||
          isNum(business)
        );
      },
      isCreatorProgramPage() {
        return this.$route.path.includes('/creator-program');
      },
      businesses() {
        return this.isCreatorProgramPage ? this.$store.getters['creatorProgram/businesses'] : this.$store.getters['businesses/items'];
      },
    },
    watch: {
      contract() {
        this.isRefreshingContracts = true;
      },
      advertiser() {
        this.isRefreshingAdvertiser = true;
      },
      business() {
        this.isRefreshingBusinesses = true;
      },
      businessSearch(nextSearch) {
        if (this.isRefreshingBusinesses) {
          this.isRefreshingBusinesses = false;
          return false;
        }

        if (isStr(nextSearch)) {
          businessSearchDebouncer(() => this.getBusinesses(nextSearch));
        }
      },
      contractSearch(nextSearch) {
        if (!isNull(nextSearch)) {
          if (this.isRefreshingContracts) {
            this.isRefreshingContracts = false;
            return false;
          }

          debouncer(() => this.getContracts(nextSearch));
        }
      },
      advertiserSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingAdvertiser) {
            this.isRefreshingAdvertiser = false;
            return false;
          }

          debouncer(() => this.getAdvertisers(nextSearch));
        }
      },
    },
    methods: {
      getBusinesses(search = '') {
        const query = {
          per_page: this.isCreatorProgramPage ? null : -1,
          search,
        };

        this.isLoadingBusinesses = true;

        const action = this.isCreatorProgramPage ? 'creatorProgram/getBusinesses' : 'businesses/getItems';

        return this.$store.dispatch(action, query)
          .catch(err => err)
          .finally(() => {
            this.isLoadingBusinesses = false;
          });
      },
      getContracts(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingContracts = true;
        return this.$store
          .dispatch('contracts/getItems', query)
          .then((res) => {
            this.isLoadingContracts = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingContracts = false;
            return err;
          });
      },
      getAdvertisers(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingAdvertisers = true;

        return this.$store
          .dispatch('promotions/getAdvertisers', query)
          .then((res) => {
            this.isLoadingAdvertisers = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingAdvertisers = false;
            return err;
          });
      },
      contractName(item) {
        return item.id ? item.id.toString() : '';
      },
      onFilter() {
        const {
          search,
          contract = {},
          advertiser = {},
          date,
          selectedCountries = [],
          business
        } = this;
        const formattedSearch = escapeSearch(search);
        const hasDate = !!date;
        let nextDate = date;

        if (hasDate && !isDateRange(nextDate)) {
          nextDate = `${nextDate} - ${nextDate}`;
        }

        this.$emit('filter', {
          search: formattedSearch,
          contract_id: contract?.id,
          advertiser_id: advertiser?.id,
          countries: selectedCountries,
          created_at: nextDate,
          business_id: business,
        });
      },
      onClearFilters() {
        this.search = null;
        this.contract = null;
        this.advertiser = null;
        this.selectedCountries = [];
        this.date = null;
        this.business = null;

        this.$emit('clearFilters');
      },
      // todo: delete this method when not needed
      customIdFilter(value, queryText) {
        if (!value || !queryText) return false;
        return value.toString().toLowerCase().indexOf(queryText.toString().toLowerCase()) > -1;
      },
      onChangeDate(date) {
        this.date = date;
      },
    },
  };
</script>
